import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { Credits } from '@App/app/entities/admin-panel/credits.model';
import { User } from '@App/app/entities/users/user';
import { NbMenuService, NbThemeService } from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, interval } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { RESPONSIVE_CONFIG } from 'src/app/configs/app.config';
import { versions } from 'src/environments/versions';
import { loadUserCompanies } from '../../store/user-companies/actions/user-companies.actions';
import { selectAllUserCompanies } from '../../store/user-companies/selectors/user-companies.selectors';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
  companyId: number | null;
  user: User;
  isAuth: boolean;
  isMobile: boolean;
  roles: string;
  version = versions.version;
  mediaQuery = this.themeService.onMediaQueryChange();
  loading = false;
  credits: Credits | null;
  skyfishLogo = '../assets/svg/logo_skyfish.svg';
  showDropDownMenu = false;
  showCreditsRequestDropdown = false;
  items: {
    title: string;
    link?: string;
    data: { id: string };
  }[];

  constructor(
    private themeService: NbThemeService,
    private authService: AuthService,
    private nbMenuService: NbMenuService,
    private elementRef: ElementRef,
    private store: Store,
  ) {}

  ngOnInit(): void {
    this.authService.currentEndpoint$.pipe(untilDestroyed(this)).subscribe((endpoint) => {
      this.companyId = endpoint?.id || null;
      if (this.companyId) {
        this.items = [
          { title: 'Profile', link: `/${this.companyId}/user`, data: { id: 'profile' } },
          { title: 'Log out', data: { id: 'logout' } },
        ];
      }
    });

    combineLatest([this.authService.currentEndpoint$, this.store.select(selectAllUserCompanies)])
      .pipe(untilDestroyed(this))
      .subscribe(([endpoint, userCompanies]) => {
        if (endpoint && userCompanies) {
          this.credits =
            userCompanies.find((company) => company.id === endpoint.id)?.credits || null;
        }
      });

    this.authService.authChange.pipe(untilDestroyed(this)).subscribe((authStatus) => {
      this.isAuth = authStatus;
      if (this.isAuth) {
        this.refreshCredits();
      }

      const user = this.authService.getUser();
      if (user?.id) {
        this.user = user as User;
      }
      this.authService.currentEndpoint$.pipe(untilDestroyed(this)).subscribe((endpoint) => {
        if (endpoint) {
          if (endpoint.userRoles.some((element) => element.name === 'Admin')) {
            this.roles = 'Admin';
          } else {
            this.roles = endpoint.userRoles
              .filter((value) => {
                if (value.name !== 'StandardUser') {
                  return value;
                }
              })
              .map((role) => role.name)
              .join(', ');
          }
        }
      });
    });

    this.refreshCreditsPeriodically();

    this.nbMenuService
      .onItemClick()
      .pipe(
        filter(({ tag }) => tag === 'profile-menu'),
        map(
          ({
            item: {
              data: { id },
            },
          }) => id,
        ),
      )
      .pipe(untilDestroyed(this))
      .subscribe((id) => {
        if (id === 'logout') {
          this.onSignOut();
        }
      });

    this.mediaQuery.pipe(untilDestroyed(this)).subscribe(([, currentMediaQuery]) => {
      this.isMobile = currentMediaQuery.width < RESPONSIVE_CONFIG.MIN_WIDTH;
    });
  }

  private refreshCredits() {
    this.store.dispatch(loadUserCompanies());
  }

  private refreshCreditsPeriodically() {
    // The value of credits might change for various reasons, other than user direct actions:
    // - build process has failed
    // - another user has started a build process
    // - admin has updated credits settings
    //
    // Since we don't have websockets integration yet, we need to periodically refresh credits value.

    interval(30000)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.isAuth) {
          this.refreshCredits();
        }
      });
  }

  onSignOut() {
    this.loading = true;
    this.authService
      .logout()
      .pipe(untilDestroyed(this))
      .subscribe(
        () => {
          this.loading = false;
          this.authService.onLogout();
        },
        () => {
          this.loading = false;
          this.authService.onLogout();
        },
      );
  }

  isUserInOtherCompanies() {
    return this.authService.isUserInOtherCompanies();
  }

  hasAccessToAdminPanel() {
    return !!this.authService.hasAccessToAdminPanel();
  }

  get companyLogo() {
    return this.authService.getCurrentEndpoint()?.logo || '';
  }

  toggleMenu() {
    this.showDropDownMenu = !this.showDropDownMenu;
    this.showCreditsRequestDropdown = false;
  }

  toggleCreditsRequestDropdown() {
    this.showCreditsRequestDropdown = !this.showCreditsRequestDropdown;
    this.showDropDownMenu = false;
  }

  @HostListener('document:click', ['$event'])
  onClick(event: Event) {
    const companySearchElement = document.getElementById('company-search');
    const targetElement = this.elementRef.nativeElement?.children[0]?.children[1]?.children[2];

    if (
      targetElement?.contains &&
      !targetElement.contains(event.target) &&
      !(event.target === companySearchElement)
    ) {
      this.showDropDownMenu = false;
      this.showCreditsRequestDropdown = false;
    }
  }
}
